import React, { useState, useCallback } from 'react';

import { PrimaryButton, InlineTextButton, IconSpinner, NamedLink } from '../../components';
import { quizRecommend } from '../../util/api';
import { trackGA4Event, GA4_EVENTS } from '../../analytics/ga4';

import { STEPS, TOW_FIT_LABEL } from './quizContent';
import css from './QuizPage.module.css';

// Read a Meta browser-id cookie for CAPI match quality (best effort).
const cookieVal = name => {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : undefined;
};

const genEventId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {
    /* noop */
  }
  return (
    'q-' +
    Date.now() +
    '-' +
    Math.random()
      .toString(36)
      .slice(2)
  );
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// ------------------------------------------------------------------
// The interactive "What Trailer Do I Need?" wizard.
//   Steps -> AI teaser -> email unlock -> full results + matching listings.
// Fully self-contained; posts to /api/quiz/recommend (server-side Grok).
// ------------------------------------------------------------------
const QuizWizard = () => {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({ hauling: '', towVehicle: '', distance: '', zip: '' });
  const [phase, setPhase] = useState('quiz'); // quiz | teaser | results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teaser, setTeaser] = useState(null);
  const [results, setResults] = useState(null);
  const [email, setEmail] = useState('');
  const [emailErr, setEmailErr] = useState(null);
  const [sessionId] = useState(genEventId);

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  const setAnswer = useCallback(val => setAnswers(a => ({ ...a, [step.key]: val })), [step.key]);

  const currentVal = answers[step.key] || '';
  const stepValid = step.zip ? /\b\d{5}\b/.test(currentVal) : currentVal.trim().length > 0;

  const metaIds = () => ({
    eventId: genEventId(),
    fbp: cookieVal('_fbp'),
    fbc: cookieVal('_fbc'),
    pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
  });

  // Advance a step, or submit for the teaser on the last step.
  const next = async () => {
    if (!stepValid) return;
    if (stepIdx === 0) trackGA4Event(GA4_EVENTS.QUIZ_START, {});
    if (!isLast) {
      trackGA4Event(GA4_EVENTS.QUIZ_STEP, { step: stepIdx + 1, field: step.key });
      setStepIdx(i => i + 1);
      return;
    }
    // Last step -> get the teaser.
    setLoading(true);
    setError(null);
    try {
      const res = await quizRecommend({ ...answers, stage: 'teaser', sessionId });
      setTeaser(res);
      setPhase('teaser');
      trackGA4Event(GA4_EVENTS.QUIZ_TEASER, {
        trailer_type: res && res.recommendedLabel,
        tow_fit: res && res.towFit,
        nearby_count: res && res.nearbyCount,
      });
    } catch (e) {
      setError('Something hiccuped on our end. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const back = () => {
    setError(null);
    if (phase === 'teaser') {
      setPhase('quiz');
      return;
    }
    setStepIdx(i => Math.max(0, i - 1));
  };

  const onKeyDown = e => {
    if (e.key === 'Enter' && !step.long) {
      e.preventDefault();
      next();
    }
  };

  // Unlock: submit email -> full results + listings.
  const unlock = async () => {
    const em = email.trim();
    if (!EMAIL_RE.test(em)) {
      setEmailErr('Please enter a valid email so we can send your results.');
      return;
    }
    setEmailErr(null);
    setLoading(true);
    setError(null);
    try {
      const res = await quizRecommend({
        ...answers,
        stage: 'unlock',
        email: em,
        sessionId,
        audience: 'renter',
        ...metaIds(),
      });
      // Browser-side Meta pixel Lead (deduped server-side by eventId in body).
      try {
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', { content_name: 'trailer_quiz', lead_type: 'renter' });
        }
      } catch (e) {
        /* noop */
      }
      setResults(res);
      setPhase('results');
      trackGA4Event(GA4_EVENTS.QUIZ_UNLOCK, {
        trailer_type: res && res.recommendation && res.recommendation.recommendedLabel,
        nearby_count: res && res.nearbyCount,
      });
    } catch (e) {
      setError('We could not unlock your results just now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setAnswers({ hauling: '', towVehicle: '', distance: '', zip: '' });
    setStepIdx(0);
    setPhase('quiz');
    setTeaser(null);
    setResults(null);
    setEmail('');
    setError(null);
  };

  // ---- render: quiz steps ----
  if (phase === 'quiz') {
    return (
      <div className={css.wizard}>
        <div className={css.progressRow}>
          {STEPS.map((s, i) => (
            <span
              key={s.key}
              className={i <= stepIdx ? css.dotActive : css.dot}
              aria-hidden="true"
            />
          ))}
          <span className={css.progressText}>
            Step {stepIdx + 1} of {STEPS.length}
          </span>
        </div>

        <h2 className={css.stepTitle}>{step.title}</h2>
        <p className={css.stepHint}>{step.hint}</p>

        {step.long ? (
          <textarea
            className={css.textarea}
            value={currentVal}
            onChange={e => setAnswer(e.target.value)}
            placeholder={step.placeholder}
            rows={3}
            autoFocus
            maxLength={600}
          />
        ) : (
          <input
            className={css.input}
            type={step.zip ? 'tel' : 'text'}
            inputMode={step.zip ? 'numeric' : 'text'}
            value={currentVal}
            onChange={e => setAnswer(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={step.placeholder}
            autoFocus
            maxLength={step.zip ? 10 : 200}
          />
        )}

        {error ? <p className={css.errorText}>{error}</p> : null}

        <div className={css.controls}>
          {stepIdx > 0 ? (
            <InlineTextButton type="button" className={css.backBtn} onClick={back}>
              Back
            </InlineTextButton>
          ) : (
            <span />
          )}
          <PrimaryButton
            type="button"
            className={css.nextBtn}
            disabled={!stepValid || loading}
            inProgress={loading}
            onClick={next}
          >
            {isLast ? 'Get my trailer match' : 'Next'}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  // ---- render: teaser (email gate) ----
  if (phase === 'teaser' && teaser) {
    const fit = TOW_FIT_LABEL[teaser.towFit] || TOW_FIT_LABEL.need_more_info;
    const loc = teaser.location;
    const locLabel =
      loc && (loc.city || loc.state)
        ? [loc.city, loc.state].filter(Boolean).join(', ')
        : 'your area';
    const count = teaser.nearbyCount;
    return (
      <div className={css.wizard}>
        <div className={css.teaserCard}>
          <span className={css.teaserEyebrow}>Your trailer match</span>
          <h2 className={css.teaserHeadline}>{teaser.teaser}</h2>

          <div className={css.recPill}>
            Recommended: <strong>{teaser.recommendedLabel}</strong>
          </div>

          {teaser.towVerdict ? (
            <div className={`${css.towBox} ${css['tow_' + fit.tone]}`}>
              <span className={css.towLabel}>{fit.label}</span>
              <p className={css.towVerdict}>{teaser.towVerdict}</p>
            </div>
          ) : null}

          <div className={css.gate}>
            {typeof count === 'number' && count > 0 ? (
              <p className={css.gateLead}>
                We found{' '}
                <strong>
                  {count} matching {count === 1 ? 'trailer' : 'trailers'}
                </strong>{' '}
                near {locLabel}. Enter your email to see the results and any new trailers near you.
              </p>
            ) : (
              <p className={css.gateLead}>
                No matching trailers are listed near {locLabel} just yet. Leave your email and we
                will shoot you a quick note the moment one lands near you.
              </p>
            )}

            <div className={css.emailRow}>
              <input
                className={css.emailInput}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') unlock();
                }}
                placeholder="you@email.com"
                autoFocus
              />
              <PrimaryButton
                type="button"
                className={css.unlockBtn}
                disabled={loading}
                inProgress={loading}
                onClick={unlock}
              >
                View my results now
              </PrimaryButton>
            </div>
            {emailErr ? <p className={css.errorText}>{emailErr}</p> : null}
            {error ? <p className={css.errorText}>{error}</p> : null}
            <p className={css.gateFine}>Free. No spam. Unsubscribe anytime.</p>
          </div>

          <InlineTextButton type="button" className={css.backBtn} onClick={back}>
            Back to my answers
          </InlineTextButton>
        </div>
      </div>
    );
  }

  // ---- render: full results ----
  if (phase === 'results' && results) {
    const rec = results.recommendation || {};
    const fit = TOW_FIT_LABEL[rec.towFit] || TOW_FIT_LABEL.need_more_info;
    const listings = results.listings || [];
    const count = results.nearbyCount;
    const loc = results.location;
    const locLabel =
      loc && (loc.city || loc.state)
        ? [loc.city, loc.state].filter(Boolean).join(', ')
        : 'your area';
    return (
      <div className={css.wizard}>
        <div className={css.resultsCard}>
          <span className={css.teaserEyebrow}>Your results</span>
          <h2 className={css.resultsHeadline}>You need a {rec.recommendedLabel}</h2>
          {rec.why ? <p className={css.resultsWhy}>{rec.why}</p> : null}
          {rec.sizeGuidance ? (
            <p className={css.resultsSize}>
              <strong>Size to look for:</strong> {rec.sizeGuidance}
            </p>
          ) : null}
          {rec.alternateLabel ? (
            <p className={css.resultsAlt}>
              Also works: <strong>{rec.alternateLabel}</strong>
            </p>
          ) : null}

          {rec.towVerdict ? (
            <div className={`${css.towBox} ${css['tow_' + fit.tone]}`}>
              <span className={css.towLabel}>Can you tow it? {fit.label}</span>
              <p className={css.towVerdict}>{rec.towVerdict}</p>
            </div>
          ) : null}

          <div className={css.listingsBlock}>
            {count > 0 ? (
              <h3 className={css.listingsTitle}>
                {count} {rec.recommendedLabel}
                {count === 1 ? '' : 's'} for rent near {locLabel}
              </h3>
            ) : (
              <div className={css.noInventory}>
                <h3 className={css.listingsTitle}>None near {locLabel} yet</h3>
                <p>
                  You are on the list. We will email you the moment a {rec.recommendedLabel} is
                  listed near you. In the meantime, browse everything available across the map.
                </p>
              </div>
            )}

            {listings.length > 0 ? (
              <div className={css.listingGrid}>
                {listings.map(l => (
                  <a
                    key={l.id}
                    className={css.listingCard}
                    href={`/l/${l.slug}/${l.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div
                      className={css.listingImg}
                      style={l.image ? { backgroundImage: `url(${l.image})` } : undefined}
                    />
                    <div className={css.listingMeta}>
                      <span className={css.listingCardTitle}>{l.title}</span>
                      {l.city || l.state ? (
                        <span className={css.listingLoc}>
                          {[l.city, l.state].filter(Boolean).join(', ')}
                        </span>
                      ) : null}
                    </div>
                  </a>
                ))}
              </div>
            ) : null}

            <a className={css.browseAllBtn} href={results.searchUrl || '/s'}>
              {count > 0 ? 'Browse all matching trailers near me' : 'Browse all trailers'}
            </a>
          </div>

          <InlineTextButton type="button" className={css.restartBtn} onClick={restart}>
            Start over
          </InlineTextButton>
        </div>
      </div>
    );
  }

  // Fallback (should not hit).
  return (
    <div className={css.wizard}>
      <IconSpinner />
    </div>
  );
};

export default QuizWizard;
